import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { formatRupiah, formatDate } from "@/lib/format";
import { WidgetHead, shortRp } from "./ChartWidgets";
import { SupplierName } from "@/components/SupplierName";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function DueCalendar({ items, paidItems = [], today }) {
  const [ym, setYm] = useState(() => ({ y: +today.slice(0, 4), m: +today.slice(5, 7) - 1 }));
  const [sel, setSel] = useState(null);
  const byDate = useMemo(() => {
    const map = {};
    [...items, ...paidItems].forEach((it) => { (map[it.date] = map[it.date] || []).push(it); });
    return map;
  }, [items, paidItems]);

  const first = new Date(ym.y, ym.m, 1).getDay();
  const offset = (first + 6) % 7;
  const days = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const shift = (d) => { setSel(null); setYm(({ y, m }) => { const n = m + d; return { y: y + Math.floor(n / 12), m: ((n % 12) + 12) % 12 }; }); };
  const selItems = sel ? byDate[sel] || [] : [];
  const next = items.find((it) => it.date >= today);
  const jump = () => { setYm({ y: +next.date.slice(0, 4), m: +next.date.slice(5, 7) - 1 }); setSel(next.date); };

  return (
    <Card className="p-6 h-full dash-rise" data-testid="due-calendar-card">
      <WidgetHead icon={CalendarDays} title="Kalender Jatuh Tempo" sub={`${MONTHS[ym.m]} ${ym.y}`}>
        <div className="flex gap-1">
          <button onClick={() => shift(-1)} className="ref-icon-btn" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => shift(1)} className="ref-icon-btn" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </WidgetHead>
      <div className="grid grid-cols-7 gap-1 text-center">
        {DAYS.map((d) => <div key={d} className="text-[10px] text-zinc-400 pb-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const key = iso(ym.y, ym.m, d);
          const has = byDate[key];
          const allPaid = has && has.every((x) => x.paid);
          const isToday = key === today;
          const active = sel === key;
          return (
            <button key={key} onClick={() => setSel(active ? null : key)} data-testid={`calendar-day-${key}`}
              className={`relative h-9 rounded-xl text-xs font-semibold transition-colors ${active ? "bg-zinc-900 text-white" : allPaid ? "bg-emerald-50 text-emerald-600 line-through decoration-2 hover:bg-emerald-100" : has ? "bg-blue-50 text-blue-700 hover:bg-blue-100" : "text-zinc-600 hover:bg-white"} ${isToday && !active ? "ring-1 ring-zinc-900" : ""}`}>
              {d}
              {has && <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full ${active ? "bg-white" : allPaid ? "bg-emerald-500" : key < today ? "bg-rose-500" : "bg-indigo-500"}`} />}
            </button>
          );
        })}
      </div>
      <div className="mt-4 min-h-[56px]" data-testid="calendar-detail">
        {sel ? (selItems.length === 0 ? <div className="text-xs text-slate-400" data-testid="calendar-day-empty">{formatDate(sel)}: tidak ada BG/transfer jatuh tempo.</div> : selItems.map((it, i) => (
          <div key={i} className={`flex items-center justify-between text-xs py-1.5 border-b border-slate-100 last:border-0 ${it.paid ? "opacity-70" : ""}`} data-testid={it.paid ? "calendar-paid-item" : "calendar-due-item"}>
            <div><div className={`font-semibold text-slate-900 ${it.paid ? "line-through decoration-emerald-500" : ""}`}><SupplierName name={it.supplier_name} /></div><div className="text-slate-400">{formatDate(it.date)} · {it.label}{it.paid && <span className="ml-1.5 rounded-full bg-emerald-100 text-emerald-700 px-1.5 py-0.5 font-semibold no-underline">Lunas{it.paid_date && it.paid_date !== it.date ? ` ${formatDate(it.paid_date)}` : ""}</span>}</div></div>
            <div className={`font-mono font-semibold ${it.paid ? "text-emerald-600 line-through" : "text-slate-900"}`}>{formatRupiah(it.amount)}</div>
          </div>
        ))) : <div className="text-xs text-slate-400">Klik tanggal bertanda untuk melihat BG/transfer yang jatuh tempo. Bulan ini: <span className="font-semibold text-slate-700">{shortRp(Object.entries(byDate).filter(([k]) => k.startsWith(iso(ym.y, ym.m, 1).slice(0, 7))).reduce((a, [, v]) => a + v.filter((x) => !x.paid).reduce((s, x) => s + x.amount, 0), 0))}</span>
          {next && <button onClick={jump} className="mt-2 block rounded-full bg-white shadow-sm text-zinc-900 px-3 py-1 text-[11px] font-medium hover:bg-zinc-50" data-testid="calendar-jump-next">Lompat ke jatuh tempo berikutnya · {formatDate(next.date)}</button>}
        </div>}
      </div>
    </Card>
  );
}
